import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { getErrorMessage } from '../../lib/errorMessage';
import { usd, primaryLabel, featureStateLabel, lockedActionLabel, listFrom } from '../commercial/commercialFormat';
import { renderUsage, allowanceLine, progressFraction, entitlementValueType, isEntitledNumericLimit } from '../commercial/usageStatus';
import TenantIntegrations from '../commercial/TenantIntegrations';
import BusinessCreditsUsage from './BusinessCreditsUsage';
import BusinessAddonRequests from './BusinessAddonRequests';
import { RefreshCw, AlertTriangle, CheckCircle2, LogIn, Loader2 } from 'lucide-react';
import { selectCurrentPlan } from '../../lib/planProjection';
import UsageMetricCard from './UsageMetricCard';

/**
 * SACP — authoritative tenant Business commercial surface (Billing / Features / Integrations).
 *
 * P0 fix: uses the central CRMAuth `makeAuthenticatedRequest` (fresh Firebase getIdToken() +
 * forced getIdToken(true) + single retry on 401) — NOT a stale localStorage token. A failed/expired
 * session is rendered HONESTLY ("Unavailable" / "session expired"), never as Active/zero-usage/empty.
 * Server-derived tenant identity via /billing/me/* (no client-supplied business id, no self-grant).
 */
const BusinessCommercial = () => {
  const { makeAuthenticatedRequest, isLoading } = useCRMAuth();
  const navigate = useNavigate();
  // authState: initializing | loading | ok | expired | error
  const [authState, setAuthState] = useState('initializing');
  const [ent, setEnt] = useState(null);
  const [features, setFeatures] = useState([]);
  const [usage, setUsage] = useState(null);   // GOV13 Step 6 canonical usage projection (/billing/me/usage)
  const [toast, setToast] = useState(null);
  const [redirected, setRedirected] = useState(false);

  const load = useCallback(async () => {
    setAuthState('loading'); setToast(null);
    try {
      // one coordinated fetch per endpoint; makeAuthenticatedRequest does a single forced-refresh
      // retry internally, so there is no 401 storm and no duplicate replay.
      // Integrations now load inside the TenantIntegrations tab (full management surface), so this
      // top-level fetch only needs entitlements (plan+usage) and features.
      const [e, f, u] = await Promise.all([
        makeAuthenticatedRequest('/api/v1/billing/me/entitlements'),
        makeAuthenticatedRequest('/api/v1/billing/me/features'),
        makeAuthenticatedRequest('/api/v1/billing/me/usage'),
      ]);
      if ([e, f].some((r) => r && r.status === 401)) { setAuthState('expired'); return; }
      if (!e || !e.ok) { setAuthState('error'); return; }
      setEnt(await e.json());
      const fData = f && f.ok ? await f.json() : null;
      setFeatures(fData ? (fData.features || listFrom(fData)) : []);
      setUsage(u && u.ok ? await u.json() : null);   // read-only projection; a failure yields no usage, never a false zero
      setAuthState('ok');
    } catch (_) {
      setAuthState('error');
    }
  }, [makeAuthenticatedRequest]);

  useEffect(() => {
    if (isLoading) { setAuthState('initializing'); return; }   // wait for Firebase auth init
    load();
  }, [isLoading, load]);

  const configureFeature = async (featureKey) => {
    try {
      const r = await makeAuthenticatedRequest(`/api/v1/billing/me/features/${encodeURIComponent(featureKey)}/configure`, {
        method: 'POST', body: JSON.stringify({ config: {} }),
      });
      if (r.status === 401) { setAuthState('expired'); return; }
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setToast({ ok: false, msg: getErrorMessage({ data: d, status: r.status }, `Request failed (HTTP ${r.status}).`) });
        return;
      }
      setToast({ ok: true, msg: 'Capability configured.' });
      await load();
    } catch (_) { setToast({ ok: false, msg: 'Network error. Please retry.' }); }
  };

  const Pill = ({ children, tone = 'gray' }) => {
    const tones = { green: 'bg-green-100 text-green-800', gray: 'bg-gray-100 text-gray-600', amber: 'bg-amber-100 text-amber-800', purple: 'bg-purple-100 text-purple-800', blue: 'bg-blue-100 text-blue-800', red: 'bg-red-100 text-red-800' };
    return <Badge className={tones[tone]}>{children}</Badge>;
  };

  // ── honest non-OK states — never render false commercial data ──
  if (authState === 'initializing') return <CenterState icon={Loader2} spin title="Restoring your secure session…" />;
  if (authState === 'loading') return <CenterState icon={Loader2} spin title="Loading your plan & capabilities…" />;
  if (authState === 'expired') {
    return (
      <CenterState icon={LogIn} title="Your session has expired."
        subtitle="Please sign in again to view your plan and capabilities.">
        <Button onClick={() => { if (!redirected) { setRedirected(true); navigate('/login'); } }}>
          <LogIn className="h-4 w-4 mr-1" />Sign in
        </Button>
      </CenterState>
    );
  }
  if (authState === 'error') {
    return (
      <CenterState icon={AlertTriangle} title="Plan and capability information is temporarily unavailable."
        subtitle="This is a loading problem, not a change to your plan.">
        <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-1" />Retry</Button>
      </CenterState>
    );
  }

  // ── authState === 'ok' : real authenticated data ──
  const plan = selectCurrentPlan(ent) || {};   // ONE shared current-plan selector (identical to Billing.jsx)
  const canonical = ent?.usage_canonical || {};    // honest per-metric status; never a false zero
  // D3: never surface the raw business_id as the business name; use a friendly fallback.
  const bizName = primaryLabel(ent?.tenant_display_name, 'Your business');

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing &amp; Capabilities</h1>
          <p className="text-gray-600 mt-1">{bizName}</p>
        </div>
        <Button onClick={load} variant="outline"><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
      </div>

      {toast && <div className={`flex items-center gap-2 p-3 rounded-lg border ${toast.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`} role="status">{toast.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}<span className="text-sm">{toast.msg}</span></div>}

      <Tabs defaultValue="billing">
        <TabsList>
          <TabsTrigger value="billing">Plan &amp; Usage</TabsTrigger>
          <TabsTrigger value="credits">Credits &amp; Usage</TabsTrigger>
          <TabsTrigger value="addons">Add-ons</TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
        </TabsList>

        {/* D18-D — tenant-safe credits, usage, rate card, packs (server projection; read-only) */}
        <TabsContent value="credits" className="pt-4">
          <BusinessCreditsUsage makeAuthenticatedRequest={makeAuthenticatedRequest} />
        </TabsContent>

        {/* GOV13-A3 — governed add-on request surface (request grants nothing; server-authoritative) */}
        <TabsContent value="addons" className="pt-4">
          <BusinessAddonRequests makeAuthenticatedRequest={makeAuthenticatedRequest} />
        </TabsContent>

        <TabsContent value="billing" className="pt-4 space-y-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Current Plan</CardTitle></CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm">
                <Row k="Business" v={bizName} />
                {/* DR-008/gap-J: never surface the raw business/brand ID to tenant users — the
                    business NAME row above is the identity; the canonical id stays internal. */}
                <Row k="Plan" v={plan.display_name || '—'} />
                <Row k="Version" v={plan.version || '—'} />
                <Row k="Price" v={plan.price_cents == null ? 'Custom' : `${usd(plan.price_cents)}/${(plan.cadence || 'monthly').replace(/ly$/, '')}`} />
                <Row k="Status" v={<Pill tone={plan.grandfathered ? 'purple' : 'green'}>{plan.status_label || (plan.display_name ? 'Active' : '—')}</Pill>} />
                {ent?.trial?.trial_end_at && <Row k="Trial ends" v={ent.trial.trial_end_at} />}
              </dl>
              {/* Step 4: receipts live in Billing (single owner) — link, never a duplicated table. */}
              <Button variant="link" className="mt-2 h-auto p-0 text-sm text-blue-600" data-testid="view-receipts-in-billing"
                onClick={() => navigate('/business/billing')}>
                View purchase receipts in Billing →
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Usage</CardTitle>
              {usage?.calculated_at && (
                <p className="text-xs text-gray-400 mt-1">As of {String(usage.calculated_at).slice(0, 16).replace('T', ' ')} · billing period {usage.billing_period}</p>
              )}
            </CardHeader>
            <CardContent>
              {/* GOV13 Step 6 — the five truthful usage authorities from the ONE shared projection
                  (/billing/me/usage). Each card shows LIVE / CALCULATING / UNAVAILABLE honestly; a missing value
                  is never a false zero and a missing allowance is never Unlimited. */}
              {usage?.usage ? (
                <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="usage-projection-grid">
                  {['max_staff', 'monthly_engagements', 'monthly_ai_resolutions', 'active_integrations', 'storage_gb']
                    .map((k) => <UsageMetricCard key={k} metric={usage.usage[k]} />)}
                </div>
                {/* GOV-7 commercial Phase B — governed FLEET usage (active-resource count vs effective limit) from
                    the SAME server projection. Allowance is rendered STRICTLY from the backend (NO client math):
                    Unlimited when the resolver says so, "Not configured" when the plan does not cap it — NEVER 0. */}
                {(usage.usage.active_vehicles || usage.usage.active_trackers) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3" data-testid="fleet-usage-grid">
                    {['active_vehicles', 'active_trackers'].map((k) => {
                      const m = usage.usage[k];
                      if (!m) return null;
                      const allow = m.unlimited ? 'Unlimited'
                        : (m.effective_allowance == null ? 'Not configured' : m.effective_allowance);
                      return (
                        <div key={k} className="border rounded-lg p-3 bg-white" data-testid={`fleet-${k}-card`}>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900">{m.display_name}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${m.source_status === 'LIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`} data-testid={`fleet-${k}-status`}>{m.source_status}</span>
                          </div>
                          {m.source_status === 'LIVE' ? (
                            <div className="mt-1 text-lg font-semibold" data-testid={`fleet-${k}-value`}>
                              <span data-testid={`fleet-${k}-used`}>{m.used}</span>
                              <span className="text-sm text-gray-400"> / <span data-testid={`fleet-${k}-allowance`}>{allow}</span> active</span>
                            </div>
                          ) : (
                            <div className="mt-1 text-sm text-gray-500" data-testid={`fleet-${k}-unavailable`}>Usage unavailable</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                </>
              ) : (
                <p className="text-sm text-gray-500" data-testid="usage-unavailable">Usage is temporarily unavailable. This is a loading problem, not a change to your plan.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="features" className="pt-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Features</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              {features.length === 0 ? <p className="text-sm text-gray-500">No features available on your plan.</p>
                : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b"><tr>{['Feature', 'Category', 'State', 'Action'].map((h) => <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {features.map((f) => {
                        // NUMERIC LIMITS (staff/engagements/AI/storage/slots) are NOT Boolean features:
                        // when the tenant has a positive/Unlimited allowance they must show usage, never
                        // "Locked/Upgrade". Classified from the canonical metric, not the display name.
                        const cm = canonical[f.feature_key];
                        const allowance = cm ? cm.limit : undefined;
                        const numeric = entitlementValueType(f) === 'numeric_limit';
                        const entitledNumeric = numeric && isEntitledNumericLimit(f, allowance);
                        const st = featureStateLabel(f.business_plane_state);
                        const locked = lockedActionLabel(f.action);
                        return (
                          <tr key={f.feature_key} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-900">{primaryLabel(f.feature_display_name, f.feature_key)}</td>
                            <td className="px-3 py-2">{numeric ? 'numeric limit' : (f.commercial_category || '—')}</td>
                            <td className="px-3 py-2">
                              {entitledNumeric
                                ? <span className="text-xs text-gray-700">{renderUsage(cm, { unit: f.feature_key === 'storage_gb' ? 'GB' : undefined })}</span>
                                : <Pill tone={st.tone}>{st.label}</Pill>}
                            </td>
                            <td className="px-3 py-2">
                              {entitledNumeric
                                ? <span className="text-xs text-gray-400">{allowanceLine(cm, { unit: f.feature_key === 'storage_gb' ? 'GB' : undefined, perPeriod: f.feature_key === 'monthly_ai_resolutions' || f.feature_key === 'monthly_engagements' }) || '—'}</span>
                                : f.business_plane_state === 'CONFIGURE_REQUIRED'
                                ? <Button size="sm" variant="outline" onClick={() => configureFeature(f.feature_key)}>Configure</Button>
                                : locked ? <span className="text-xs text-gray-600">{locked}</span>
                                : f.business_plane_state === 'IN_DEVELOPMENT' ? <span className="text-xs text-amber-600">In development</span>
                                : <span className="text-xs text-gray-400">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
            </CardContent>
          </Card>
          <p className="text-xs text-gray-400 mt-2">Locked features show the exact upgrade or add-on path. You cannot grant yourself a capability — configuration is only enabled for entitled features.</p>
        </TabsContent>

        <TabsContent value="integrations" className="pt-4">
          {/* Full tenant-safe integration management: real create/configure/credentials/enable/
              disable/test/remove against /billing/me/connectors/* (secret write-only, slot-enforced). */}
          <TenantIntegrations request={makeAuthenticatedRequest} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Row = ({ k, v }) => (
  <div className="flex justify-between py-1 border-b border-gray-100"><dt className="text-gray-500">{k}</dt><dd className="text-gray-900 font-medium">{v}</dd></div>
);
const CenterState = ({ icon: Icon, spin, title, subtitle, children }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <Icon className={`h-8 w-8 text-gray-400 mb-3 ${spin ? 'animate-spin' : ''}`} />
    <p className="text-gray-800 font-medium">{title}</p>
    {subtitle && <p className="text-sm text-gray-500 mt-1 max-w-md">{subtitle}</p>}
    {children && <div className="mt-4">{children}</div>}
  </div>
);

export default BusinessCommercial;
